import { useCallback, useEffect, useState } from "react";
// Helpers
import { errorLogger, getError } from "@/Common/helpers/logger";
import { getEmptyColumnDefinition, getCellClassNameFn, isEmptyRow, isFooterCell } from "@/Common/helpers/Tables";
// Components
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
// Types and constants
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
import { HEADER_CLASS_NAME, ID_GRID_COLDEF } from "@/Washouts/constants";
import { CITRecord, CITTableProps } from "@/Washouts/types/finance";

const emptyData: CITRecord[] = [
	{
		_id: "Delivered",
		dealStatus: "Delivered",
		fundedDealsAmount: 0,
		totalDealsAmount: 0,
		averageDaysInTransit: Number(0),
		individualEfficiency: Number(0),
		storeEfficiency: Number(0),
	},
	{
		_id: "Closed",
		dealStatus: "Closed",
		fundedDealsAmount: 0,
		totalDealsAmount: 0,
		averageDaysInTransit: Number(0),
		individualEfficiency: Number(0),
		storeEfficiency: Number(0),
	},
	{
		_id: "Finalized",
		dealStatus: "Finalized",
		fundedDealsAmount: 0,
		totalDealsAmount: 0,
		averageDaysInTransit: Number(0),
		individualEfficiency: Number(0),
		storeEfficiency: Number(0),
	},
];

function CITTable(props: Readonly<CITTableProps>) {
	const title = props.title ?? "CIT Performance (MTD)";
	const apiRef = useGridApiRef();

	const [totalFundedDeals, setTotalFundedDeals] = useState(0);
	const [totalDeals, setTotalDeals] = useState(0);
	const [totalAverageDaysInTransit, setTotalAverageDaysInTransit] = useState(0);
	const [totalIndividualEfficiency, setTotalIndividualEfficiency] = useState(0);
	const [totalStoreAverageDaysInTransit, setTotalStoreAverageDaysInTransit] = useState(0);
	const [totalStoreEfficiency, setTotalStoreEfficiency] = useState(0);

	const [data, setData] = useState<CITRecord[]>([]);

	const columns: GridColDef[] = [
		ID_GRID_COLDEF,
		{
			field: "dealStatus",
			headerName: "Deal Status",
			width: 70,
			filterable: false,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn([]),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return <TypographyCell params={params}>Totals</TypographyCell>;
				}
				return <TypographyCell params={params} />;
			},
		},
		{
			field: "fundedDealsAmount",
			headerName: "# of Funded Deals",
			type: "number",
			width: 90,
			filterable: false,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader params={params} right />,
			cellClassName: getCellClassNameFn([]),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					const funded = totalFundedDeals !== 0 ? totalFundedDeals : params?.value;
					return <TypographyCell params={params}>{funded}</TypographyCell>;
				}
				return <TypographyCell params={params} />;
			},
		},
		{
			field: "totalDealsAmount",
			headerName: "Total # of Deals",
			type: "number",
			width: 80,
			filterable: false,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader params={params} right />,
			cellClassName: getCellClassNameFn([]),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					const deals = totalDeals !== 0 ? totalDeals : params?.value;
					return <TypographyCell params={params}>{deals}</TypographyCell>;
				}
				return <TypographyCell params={params} />;
			},
		},
		{
			field: "averageDaysInTransit",
			headerName: "Avg. Days In Transit",
			type: "number",
			width: 90,
			filterable: false,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader params={params} right />,
			cellClassName: getCellClassNameFn([]),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					const inTransit = totalAverageDaysInTransit !== 0 ? totalAverageDaysInTransit : params.value;
					return <TypographyCell params={params}>{inTransit}</TypographyCell>;
				}
				const content = isEmptyRow(params) ? undefined : params.value ?? 0;
				return <TypographyCell params={params}>{content}</TypographyCell>;
			},
		},
		{
			field: "individualEfficiency",
			headerName: "Individual Efficiency",
			type: "number",
			width: 90,
			filterable: false,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader params={params} right />,
			cellClassName: getCellClassNameFn([], { isPercentage: true }),
			renderCell: (params) => {
				let value = Number((params.value ?? 0) * 100).toFixed(2);
				if (isFooterCell(params)) {
					const individualEfficiency = totalIndividualEfficiency
						? Number(100 * totalIndividualEfficiency).toFixed(2)
						: Number(100 * params.value).toFixed(2);
					if (isNaN(Number(individualEfficiency))) {
						value = Number(0).toFixed(2);
					} else {
						value = Number(individualEfficiency).toFixed(2);
					}
				}
				return (
					<TypographyCell params={params} hints={{ isPercentage: true }}>
						{isEmptyRow(params) ? undefined : value}
					</TypographyCell>
				);
			},
		},
		getEmptyColumnDefinition(),
		{
			field: "storeAverageDaysInTransit",
			headerName: "Avg. Store CIT Days",
			type: "number",
			width: 90,
			filterable: false,
			disableReorder: true,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader params={params} right />,
			cellClassName: getCellClassNameFn([]),
			renderCell: (params) => {
				let value = params.value || 0;
				if (isFooterCell(params)) {
					value = totalStoreAverageDaysInTransit !== 0 ? totalStoreAverageDaysInTransit : value;
				}
				return <TypographyCell params={params}>{isEmptyRow(params) ? undefined : value}</TypographyCell>;
			},
		},
		{
			field: "storeEfficiency",
			headerName: "Store Efficiency",
			type: "number",
			sortable: false,
			width: 100,
			flex: 1,
			disableReorder: true,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader params={params} right />,
			cellClassName: getCellClassNameFn([], { isPercentage: true }),
			renderCell: (params) => {
				let value = Number((params.value ?? 0) * 100).toFixed(2);
				if (isFooterCell(params)) {
					const storeEfficiency = totalStoreEfficiency
						? Number(100 * totalStoreEfficiency).toFixed(2)
						: Number(100 * params.value).toFixed(2);
					if (isNaN(Number(storeEfficiency))) {
						return Number(0).toFixed(2);
					} else {
						value = Number(storeEfficiency).toFixed(2);
					}
				}
				return (
					<TypographyCell params={params} hints={{ isPercentage: true }}>
						{isEmptyRow(params) ? undefined : value}
					</TypographyCell>
				);
			},
		},
	];

	const clearFilters = useCallback(() => {
		try {
			const filter = apiRef.current?.state?.filter;
			if (filter) {
				const cleanFilter = {
					filterModel: { items: [], linkOperator: "and" },
					visibleRows: null,
					visibleRowsLookup: {},
				};

				apiRef.current.setState((prevState) => ({
					...prevState,
					filter: { ...filter, ...cleanFilter },
				}));
			}
		} catch (error) {
			errorLogger(getError(error));
		}
	}, [apiRef]);

	useEffect(() => {
		clearFilters();

		setTotalDeals(0);
		setTotalFundedDeals(0);
		setTotalAverageDaysInTransit(0);
		setTotalIndividualEfficiency(0);
		setTotalStoreAverageDaysInTransit(0);
		setTotalStoreEfficiency(0);

		if (props.data?.cits?.length && props.data.totals) {
			setData(props.data.cits);
			setTotalDeals(props.data.totals.totalDeals ?? 0);
			setTotalFundedDeals(props.data.totals.totalFundedDeals ?? 0);
			setTotalAverageDaysInTransit(props.data.totals.averageDaysInTransit ?? 0);
			setTotalIndividualEfficiency(props.data.totals.individualEfficiency ?? 0);
			setTotalStoreAverageDaysInTransit(props.data.totals.storeAverageDaysInTransit ?? 0);
			setTotalStoreEfficiency(props.data.totals.storeEfficiency ?? 0);
		} else {
			setData(emptyData);
		}
	}, [props, props.data, apiRef, clearFilters]);

	return (
		<DataSmartTable
			{...props.tableProps}
			apiRef={apiRef}
			columns={columns}
			rows={data ?? []}
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			columnVisibilityModel={{
				_id: false,
			}}
			initialState={{
				aggregation: {
					model: {
						fundedDealsAmount: "sum",
						totalDealsAmount: "sum",
						averageDaysInTransit: "avg",
						individualEfficiency: "avg",
						storeEfficiency: "avg",
					},
				},
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					customTitle: {
						hide: false,
						label: title,
						sx: { mb: "15px" },
					},
				},
				noRowsOverlay: {
					title: `No ${title} Data`,
				},
			}}
			getCellClassName={(params) => {
				// Targeting footer
				if (isFooterCell(params)) {
					// Use this to change the cell class name depending on the cell value
					const negatives = [
						"fundedDealsAmount",
						"totalDealsAmount",
						"averageDaysInTransit",
						"individualEfficiency",
						"storeEfficiency",
					];
					if (negatives.includes(params.field) && typeof params.value === "number" && params.value < 0) {
						return "aggregation-footer-negative";
					}

					// Append label 'Total' to the cell value in footer
					if (params.field === "dealStatus") {
						return "aggregation-footer-total";
					}
				}
				return "";
			}}
			customProps={{}}
		/>
	);
}

export default function CITPerformanceTable(props: Readonly<CITTableProps>) {
	if (props.loading) {
		return <TableLoader smallTitle content />;
	}

	return <>{props.dataPresent && <CITTable {...props} />}</>;
}
